import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { describeApiError } from "../lib/apiError";

export interface EditableSection<TLoaded, TDraft> {
  load: (signal: AbortSignal) => Promise<TLoaded | null>;
  toDraft: (loaded: TLoaded | null) => TDraft;
  /** Return a message to block the save, or null to proceed. */
  validate?: (draft: TDraft) => string | null;
  /**
   * `loaded` is handed in so a body can be built against the server's last-known
   * state rather than against form defaults.
   */
  submit: (draft: TDraft, loaded: TLoaded | null) => Promise<TLoaded | void>;
  deps: unknown[];
}

export interface EditableSectionState<TLoaded, TDraft> {
  loaded: TLoaded | null;
  draft: TDraft;
  patch: (partial: Partial<TDraft>) => void;
  dirty: boolean;
  loading: boolean;
  busy: boolean;
  /** Load/submit failure. */
  error: string;
  errorKind: string;
  /** Client-side validation failure; cleared on the next patch. */
  fieldError: string;
  savedAt: number | null;
  save: () => Promise<void>;
  reset: () => void;
  reload: () => void;
}

/**
 * load → dirty → validate → submit → adopt-response.
 *
 * Two properties the app's earlier one-off write did not have: the load is
 * AbortController-guarded (switching realms mid-flight would otherwise set state
 * after unmount), and a successful write adopts the server's echoed resource as
 * the new baseline — so `dirty` becomes false from server truth instead of from
 * an assumption. Write-only fields (a secret) can keep the old shape; an
 * editable value cannot.
 */
export function useEditableSection<TLoaded, TDraft>(
  section: EditableSection<TLoaded, TDraft>,
): EditableSectionState<TLoaded, TDraft> {
  const { load, toDraft, validate, submit, deps } = section;
  const [loaded, setLoaded] = useState<TLoaded | null>(null);
  const [draft, setDraft] = useState<TDraft>(() => toDraft(null));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorKind, setErrorKind] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Kept in refs so the load effect depends only on `deps` + reloadKey; the
  // callbacks are inline literals at every call site and would otherwise
  // re-trigger the fetch on every render.
  const loadRef = useRef(load);
  const toDraftRef = useRef(toDraft);
  const validateRef = useRef(validate);
  const submitRef = useRef(submit);
  loadRef.current = load;
  toDraftRef.current = toDraft;
  validateRef.current = validate;
  submitRef.current = submit;

  const adopt = useCallback((next: TLoaded | null) => {
    setLoaded(next);
    setDraft(toDraftRef.current(next));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setLoading(true);
    setError("");
    setErrorKind("");
    loadRef
      .current(controller.signal)
      .then((value) => {
        if (!live) return;
        adopt(value);
      })
      .catch((e: unknown) => {
        if (!live || controller.signal.aborted) return;
        const described = describeApiError(e);
        setError(described.message);
        setErrorKind(described.kind);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adopt, reloadKey, ...deps]);

  const baseline = useMemo(() => JSON.stringify(toDraft(loaded)), [toDraft, loaded]);
  const dirty = JSON.stringify(draft) !== baseline;

  const patch = useCallback((partial: Partial<TDraft>) => {
    setFieldError("");
    setDraft((d) => ({ ...d, ...partial }));
  }, []);

  const save = useCallback(async () => {
    setError("");
    setErrorKind("");
    setFieldError("");
    const invalid = validateRef.current?.(draft) ?? null;
    if (invalid) {
      // Return before touching `busy` — a validation failure never blocks the form.
      setFieldError(invalid);
      return;
    }
    setBusy(true);
    try {
      const echoed = await submitRef.current(draft, loaded);
      if (echoed === undefined) {
        setReloadKey((k) => k + 1);
      } else {
        adopt(echoed as TLoaded);
      }
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (e: unknown) {
      const described = describeApiError(e);
      setError(described.message);
      setErrorKind(described.kind);
    } finally {
      setBusy(false);
    }
  }, [adopt, draft, loaded]);

  return {
    loaded,
    draft,
    patch,
    dirty,
    loading,
    busy,
    error,
    errorKind,
    fieldError,
    savedAt,
    save,
    reset: () => {
      setFieldError("");
      setError("");
      setDraft(toDraftRef.current(loaded));
    },
    reload: () => setReloadKey((k) => k + 1),
  };
}
